import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Button, IconButton, Paper, Typography, List, ListItemText, Divider, ListItemButton, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEventDetails } from '../context';
import { db } from '../instant';
import ScrollableLayout from 'src/layouts/ScrollableLayout';
import { queryReportTemplates } from '../schema';
import LoadingIndicator from '../components/LoadingIndicator';
import DeleteReportDialog from '../components/DeleteReportDialog';
import { useState } from 'react';
import { Report } from 'src/schema';
import DeleteIcon from '@mui/icons-material/Delete';
import { usePermissions } from 'src/hooks/usePermissions';

export default function ReportsScreen() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { canGenerateReports } = usePermissions();
    const { event, isLoading: dataIsLoading, error: dataError } = useEventDetails(eventId || '');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<Report | undefined>(undefined);

    const { isLoading: loadingTemplates, error: templatesError } = queryReportTemplates();
    const { isLoading: authIsLoading, error: authError } = db.useAuth();

    const isLoading = dataIsLoading || authIsLoading || loadingTemplates;
    const error = dataError || authError || templatesError;

    if (isLoading) {
        return <LoadingIndicator message="Loading reports..." />;
    }

    if (error || !eventId) {
        return <Typography color="error">Error loading report data</Typography>;
    }

    const eventName = event?.name || eventId;
    // Get reports from the event object
    const reports = event?.reports || [];

    const handleBack = () => {
        navigate(`/event/${eventId}/data`);
    };

    const handleCreateReport = () => {
        navigate(`/event/${eventId}/create-report`);
    };

    const handleDeleteReport = (report: Report, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setReportToDelete(report);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <ScrollableLayout>
            <Stack direction="column" spacing={2} sx={{ my: 4, mx: 0, width: '100%', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">Reports</Typography>
                </Box>

                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 }, // Responsive padding - 16px on mobile, 24px on larger screens
                        mb: 4,
                        minWidth: '300px',
                        width: '100%',
                        maxWidth: '400px',
                        margin: '20px',
                        boxSizing: 'border-box', // Ensure padding is included in width calculation
                    }}
                >
                    <Typography sx={{ ml: { xs: 1, sm: 2 }, mb: 2 }} variant="h6" gutterBottom>
                        {eventName}
                    </Typography>

                    <Typography variant="h5" gutterBottom align="center">
                        Existing Reports
                    </Typography>
                    <Paper sx={{ mb: 3 }}>
                        {reports && reports.length > 0 ? (
                            <List>
                                {reports.map((report, index) => (
                                    <Box key={report.id}>
                                        <ListItemButton
                                            component={Link}
                                            to={`/report/${report.id}`}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={`${report.template?.name || 'Unnamed Template'}`}
                                                secondary={
                                                    <>
                                                        <Typography component="span" variant="body2" display="block">
                                                            Created: {new Date(report.createdAt).toLocaleString()}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" display="block">
                                                            Breakdown: {report.materialBreakdown ? 'by Material' : 'by Category'}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" display="block">
                                                            {report.materialBreakdown
                                                                ? `Materials selected: ${report.materials?.length || 'All'}`
                                                                : `Categories selected: ${report.categories?.length || 'All'}`}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" display="block">
                                                            Datasets selected: {report.datasets?.length || 'All'}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                            {canGenerateReports && (
                                                <IconButton onClick={e => handleDeleteReport(report, e)} edge="end" aria-label="delete">
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </ListItemButton>
                                        {index < reports.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </List>
                        ) : (
                            <Typography align="center" color="textSecondary" sx={{ py: 2 }}>
                                No reports generated yet
                            </Typography>
                        )}
                    </Paper>

                    {canGenerateReports && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button variant="contained" color="primary" onClick={handleCreateReport} sx={{ minWidth: 200 }}>
                                Generate New Report
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Stack>

            {/* Delete Report Dialog */}
            <DeleteReportDialog report={reportToDelete} open={deleteDialogOpen} onClose={handleCloseDeleteDialog} />
        </ScrollableLayout>
    );
}
